/*
 * -----------------------------------------------------------------------
 *  <copyright file="reset-password.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useContext, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import FormControl from '@mui/material/FormControl';
import { useNavigate, useParams } from 'react-router';
import getOrganisation from 'apis/get-organisation';
import { ApiLogin } from 'apis/login';
import { UserContext } from 'contexts/user-context';
import { useBoolean, useLogger, useTranslation } from 'hooks';
import { OrganisationFields } from 'queries/generated/fragments.generated';
import { UPDATE_USER_PASSWORD } from 'queries/uam';
import Settings from 'settings/settings';
import Constants from 'utils/constants';
import NotificationService from 'utils/notification';
import { validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons/button';
import CharactersRemaining from 'views/components/core/characters-remaining';
import IconButton from 'views/components/core/icon-button';
import InputAdornment from 'views/components/core/input-adornment';
import Loading from 'views/components/core/loading';
import PasswordRequirementsWarning from 'views/components/core/password-requirements-warning';
import TextField from 'views/components/core/text-field';
import { AllRoutes } from '@utils/router/all-routes';

const ResetPassword = ({
    currentPassword,
    organisation: orgInfo,
}: {
    currentPassword?: string;
    organisation?: OrganisationFields;
}) => {
    const { passwordForResetPage, user, setIsLoggedIn, setToken, setUser } = useContext(UserContext);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const { token, orgCode } = useParams();
    const Logger = useLogger();
    const showNewPassword = useBoolean(false);
    const showConfirmedPassword = useBoolean(false);
    const passwordIsInvalid = useBoolean(false);
    const disabledPasswordBtn = useBoolean(true);
    const [updateUserPassword] = useMutation(UPDATE_USER_PASSWORD);
    const [passwords, setPasswords] = useState({
        newPassword: '' as string,
        confirmedPassword: '' as string,
    } as any);
    const [organisation, setOrganisation] = useState(null);
    const [errors, setErrors] = useState({} as any);
    const charactersCount: number = 70;

    const loading = useBoolean(false);

    const loadOrganisation = () => {
        loading.setTrue();

        getOrganisation(orgCode)
            .then((response) => {
                loading.setFalse();

                return response;
            })
            .then((org) => {
                setOrganisation(org);
            })
            .catch((e) => {
                NotificationService.showNotification(e.message);
            });
    };

    useEffect(() => {
        if (orgCode && !orgInfo) loadOrganisation();
        else if (orgInfo) setOrganisation(orgInfo);
    }, [orgCode, orgInfo]);

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const validateError = validateFields(value, field, length, required);
        setErrors({
            ...errors,
            serverError: null,
            newPassword: null,
            confirmedPassword: null,
            ...validateError,
        });
    };

    const onChange = (name: string) => (event: any) => {
        disabledPasswordBtn.setFalse();
        const length = charactersCount;
        if (name === 'newPassword')
            setPasswords({
                ...passwords,
                newPassword: event.target.value.trim(),
            });
        else
            setPasswords({
                ...passwords,
                confirmedPassword: event.target.value.trim(),
            });

        validate(event.target.value.trim(), name, length, false);
    };

    // Own sendLoginRequest because ResetPassword can be independent route without props: sendLoginRequest, currentPassword
    const sendLoginRequest = async (passwordRequest: string) => {
        loading.setTrue();
        const response = await ApiLogin(user.email, passwordRequest, orgCode && orgCode.length ? orgCode : null);
        if (response) loading.setFalse();

        if (response.error) {
            if (response.error === 'Failed to fetch' || response.error.indexOf('Unexpected token < in JSON') >= 0) {
                setErrors({
                    ...errors,
                    serverError: 'Unable to contact the server. Please try again later.',
                });
            } else {
                setErrors({
                    ...errors,
                    serverError: response.error,
                });
            }
        }

        if (response.token) {
            setIsLoggedIn(true);
            setToken(response.token);
            // Logger.log(`User logged in (${user.email}) with token: ${response.token}`);
            navigate(AllRoutes.path('home'));
        } else {
            setToken(null);
            setUser(null);
            navigate(AllRoutes.path(orgCode ? 'login-organisation' : 'login', orgCode ? { orgCode } : null));
        }
    };

    const validatePassword = () => {
        const localErrors: any = {};

        const errorNewPassword = validateFields(passwords.newPassword, 'newPassword', 60, true);
        const errorConfirmedPassword = validateFields(passwords.confirmedPassword, 'confirmedPassword', 60, true);

        localErrors.newPassword = errorNewPassword.newPassword;
        localErrors.confirmedPassword = errorConfirmedPassword.confirmedPassword;

        if (!localErrors.newPassword && !localErrors.confirmedPassword) {
            if (passwordIsInvalid.value) {
                localErrors.newPassword = t('uam:incorrectPasswordFormat');
                if (passwords.confirmedPassword.length)
                    localErrors.confirmedPassword = t('uam:incorrectPasswordFormat');
            } else if (passwords.newPassword !== passwords.confirmedPassword && passwords.confirmedPassword.length) {
                localErrors.confirmedPassword = t('uam:passwordsDontMatch');
                localErrors.newPassword = t('uam:passwordsDontMatch');
            }
        }

        setErrors({
            ...errors,
            ...localErrors,
        });

        return Object.keys(localErrors).filter((item) => localErrors[item]).length > 0;
    };

    const send = async (e: any) => {
        if (validatePassword()) {
            disabledPasswordBtn.setTrue();
            return;
        }

        e.preventDefault();
        loading.setTrue();
        if (currentPassword || passwordForResetPage || !token) {
            updateUserPassword({
                variables: {
                    currentPassword: currentPassword || passwordForResetPage,
                    newPassword: passwords.confirmedPassword,
                    reason: '',
                },
            })
                .then((res: any) => {
                    loading.setFalse();
                    if (res.data.updateUserPassword?.match('Password matches existing password'))
                        NotificationService.showNotification(res.data.updateUserPassword);
                    else if (sendLoginRequest) sendLoginRequest(passwords.confirmedPassword);
                })
                .catch((error) => {
                    console.log('error', error);
                    loading.setFalse();
                    disabledPasswordBtn.setTrue();
                });
        } else {
            // No authentication needed
            fetch(`${Settings.api.url()}/api/resetpassword/`, {
                method: 'POST',
                body: JSON.stringify({
                    token,
                    newPassword: passwords.confirmedPassword,
                    orgCode,
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((result) => result.json())
                .then((result) => {
                    loading.setFalse();
                    disabledPasswordBtn.setTrue();
                    setErrors({
                        ...errors,
                        serverError: t(result?.error),
                    });

                    if (result && !result.error)
                        // Take them back to their login page
                        navigate(
                            AllRoutes.path(orgCode ? 'login-organisation' : 'login', orgCode ? { orgCode } : null),
                        );
                })
                .catch((error) => {
                    disabledPasswordBtn.setTrue();
                    setErrors({
                        ...errors,
                        serverError: t(error.message),
                    });
                });
        }
    };

    if (loading.value) return <Loading />;

    return (
        <div className="reset-password-wrap">
            <div className="reset-password-title">{t('login:resetPassword')}</div>
            <form className="form">
                <div className="margin-b dialog-warning">
                    <PasswordRequirementsWarning
                        security={orgCode ? organisation?.security : Constants.TRISecuritySettings}
                        password={passwords.newPassword}
                        onPasswordValidate={(value: boolean) => passwordIsInvalid.setValue(value)}
                    />
                </div>

                <FormControl fullWidth required>
                    <TextField
                        className="margin-b"
                        error={errors.newPassword}
                        label={`${t('uam:newPassword')}*`}
                        id="new-password"
                        type={showNewPassword.value ? 'text' : 'password'}
                        rows={1}
                        fullWidth
                        value={passwords.newPassword}
                        onChange={onChange('newPassword')}
                        placeholder={t('uam:enterYourPassword')}
                        variant="outlined"
                        color="secondary"
                        helperText={
                            errors.newPassword ? (
                                <CharactersRemaining id="new-pass-text" error={errors.newPassword} />
                            ) : null
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        id="toggle-password-visibility"
                                        aria-label={t('togglePasswordVisibility')}
                                        onClick={showNewPassword.toggle}
                                        onMouseDown={(e: any) => e.preventDefault()}
                                    >
                                        {showNewPassword.value ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        className="margin-b"
                        error={errors.confirmedPassword}
                        label={`${t('uam:reEnterNewPassword')}*`}
                        id="confirm-new-password"
                        type={showConfirmedPassword.value ? 'text' : 'password'}
                        rows={1}
                        fullWidth
                        value={passwords.confirmedPassword}
                        onChange={onChange('confirmedPassword')}
                        placeholder={t('uam:reEnterNewPassword')}
                        variant="outlined"
                        color="secondary"
                        helperText={
                            errors.confirmedPassword ? (
                                <CharactersRemaining id="confirm-pass-text" error={errors.confirmedPassword} />
                            ) : null
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        id="toggle-password-visibility-2"
                                        aria-label={t('togglePasswordVisibility')}
                                        onClick={showConfirmedPassword.toggle}
                                        onMouseDown={(e: any) => e.preventDefault()}
                                    >
                                        {showConfirmedPassword.value ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </FormControl>
                <div className="error-notification mb-15">{errors.serverError ? errors.serverError : null}</div>
                <div className="button-wrap">
                    <Button
                        text="uam:updatePassword"
                        disabled={loading.value || disabledPasswordBtn.value}
                        onClick={send}
                    />
                </div>
            </form>
        </div>
    );
};

export default ResetPassword;
